// Lightweight fetch wrapper with retries and exponential backoff.
export type FetchRetryOptions = {
  retries?: number;
  retryDelay?: number; // initial delay in ms
  factor?: number; // exponential backoff factor
  retryOn?: (res: Response | null, err: any) => boolean;
};

function wait(ms: number) {
  return new Promise((res) => setTimeout(res, ms));
}

export async function fetchWithRetry(input: RequestInfo, init?: RequestInit, opts?: FetchRetryOptions): Promise<Response> {
  const { retries = 3, retryDelay = 500, factor = 2, retryOn } = opts || {};
  let attempt = 0;
  let lastError: any = null;

  while (attempt <= retries) {
    try {
      const res = await fetch(input, init as RequestInit);
      // If a custom predicate is provided, use it to decide whether to retry
      const shouldRetry = retryOn ? retryOn(res, null) : (res.status >= 500 || res.status === 429);
      if (!shouldRetry) return res;
      lastError = new Error(`Retryable response status ${res.status}`);
    } catch (err) {
      lastError = err;
      const shouldRetry = retryOn ? retryOn(null, err) : true;
      if (!shouldRetry) throw err;
    }

    if (attempt === retries) break;
    const delay = Math.round(retryDelay * Math.pow(factor, attempt) * (0.8 + Math.random() * 0.4));
    await wait(delay);
    attempt += 1;
  }

  throw lastError || new Error('fetchWithRetry: unknown error');
}
